import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, FilePlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { CustomerDialog } from "../components/CustomerDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/customers", { params: { q } }).then((r) => setList(r.data)), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.company_name || c.contact_name}?`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Customer deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Directory" title="Customers">
        <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" /><Input data-testid="customer-search-input" placeholder="Search name, ABN, suburb…" value={q} onChange={(e) => setQ(e.target.value)} className="w-72 bg-white pl-9" /></div>
        <Button data-testid="new-customer-button" onClick={() => { setEditing(null); setOpen(true); }} className="bg-red-600 hover:bg-red-700"><Plus className="mr-1 h-4 w-4" />New customer</Button>
      </PageHeader>
      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead className="border-b border-zinc-200 bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-zinc-500">
            <tr><th className="px-5 py-3">Customer</th><th className="px-3">ABN</th><th className="px-3">Contact</th><th className="px-3">Phone</th><th className="px-3">Location</th><th className="w-36" /></tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-zinc-100 last:border-0 hover:bg-slate-50">
                <td className="px-5 py-3"><button data-testid={`customer-open-${c.id}`} onClick={() => navigate(`/customers/${c.id}`)} className="text-left font-semibold hover:text-red-600 hover:underline">{c.company_name || c.contact_name}</button><div className="text-xs capitalize text-zinc-400">{c.customer_type}</div></td>
                <td className="px-3 font-mono text-xs">{c.abn || "—"}</td>
                <td className="px-3"><div>{c.contact_name}</div><div className="text-xs text-zinc-400">{c.email}</div></td>
                <td className="px-3 font-mono text-xs">{c.phone || c.mobile}</td>
                <td className="px-3">{[c.suburb, c.state, c.postcode].filter(Boolean).join(" ")}</td>
                <td className="px-3 text-right">
                  <button data-testid={`customer-new-quote-${c.id}`} title="New quote" onClick={() => navigate(`/quotes/new?customer=${c.id}`)} className="rounded p-2 text-zinc-500 hover:bg-red-50 hover:text-red-600"><FilePlus className="h-4 w-4" /></button>
                  <button data-testid={`customer-edit-${c.id}`} title="Edit" onClick={() => { setEditing(c); setOpen(true); }} className="rounded p-2 text-zinc-500 hover:bg-zinc-100"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`customer-delete-${c.id}`} title="Delete" onClick={() => remove(c)} className="rounded p-2 text-zinc-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-400">No customers found.</td></tr>}
          </tbody>
        </table>
      </div>
      <CustomerDialog open={open} onOpenChange={setOpen} customer={editing} onSaved={load} />
    </div>
  );
}
